<template>
  <v-card height="100%" :loading="loading" flat color="bg1" class="api-keys-management overflow-y-auto">
    <!-- Page Header -->
    <v-list-item two-line :class="$vuetify.theme.isDark ? 'bg2' : 'white'">
      <v-list-item-content>
        <v-list-item-title class="title d-flex align-center">
          API Keys
          <v-tooltip bottom max-width="300">
            <template v-slot:activator="{ on, attrs }">
              <v-icon small class="ml-2" v-bind="attrs" v-on="on">mdi-information-outline</v-icon>
            </template>
            <span>Manage API keys for external integrations. Keys provide programmatic access to your data via the External API.</span>
          </v-tooltip>
        </v-list-item-title>
        <v-list-item-subtitle>Create and manage API keys for external applications</v-list-item-subtitle>
        <a :href="apiDocsUrl" target="_blank" class="api-docs-link d-inline-flex align-center mt-1">
          <v-icon small class="mr-1">mdi-file-document-outline</v-icon>
          API Documentation
          <v-icon x-small class="ml-1">mdi-open-in-new</v-icon>
        </a>
      </v-list-item-content>
      <v-list-item-action>
        <v-btn color="primary" @click="openCreateDialog">
          <v-icon left>mdi-plus</v-icon>
          Create Key
        </v-btn>
      </v-list-item-action>
    </v-list-item>
    <v-divider></v-divider>

    <!-- Data Table -->
    <v-sheet class="ma-5">
      <v-data-table
        :headers="headers"
        :items="apiKeys"
        :loading="loading"
        class="elevation-1 api-keys-table"
        :footer-props="{
          showFirstLastPage: true,
          itemsPerPageOptions: [10, 25, 50, -1],
        }">

        <!-- Actions Column -->
        <template v-slot:[`item.actions`]="{ item }">
          <v-menu dense offset-x right>
            <template v-slot:activator="{ attrs, on }">
              <v-btn icon small v-bind="attrs" v-on="on" :disabled="!item.isActive">
                <v-icon small>mdi-dots-vertical</v-icon>
              </v-btn>
            </template>
            <v-list dense>
              <v-list-item @click="openEditDialog(item)" :disabled="!item.isActive">
                <v-list-item-icon class="mr-2">
                  <v-icon small>mdi-pencil-outline</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>Edit</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-list-item @click="confirmRevoke(item)" :disabled="!item.isActive">
                <v-list-item-icon class="mr-2">
                  <v-icon small color="error">mdi-key-remove</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title class="error--text">Revoke</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-menu>
        </template>

        <!-- Name Column -->
        <template v-slot:[`item.name`]="{ item }">
          <span :class="{ 'text--disabled': !item.isActive }">{{ item.name }}</span>
        </template>

        <!-- Permission Column -->
        <template v-slot:[`item.permission`]="{ item }">
          <v-chip
            x-small
            outlined
            :color="item.permission === 'write' ? 'warning' : 'info'"
            :class="{ 'opacity-50': !item.isActive }">
            {{ item.permission }}
          </v-chip>
        </template>

        <!-- Key Prefix Column -->
        <template v-slot:[`item.keyPrefix`]="{ item }">
          <code class="text--secondary key-prefix" :class="{ 'opacity-50': !item.isActive }">
            {{ item.keyPrefix }}...
          </code>
        </template>

        <!-- Status Column -->
        <template v-slot:[`item.status`]="{ item }">
          <v-chip x-small :color="getStatusColor(item)" text-color="white">
            {{ getStatusText(item) }}
          </v-chip>
        </template>

        <!-- Last Used Column -->
        <template v-slot:[`item.lastUsedDate`]="{ item }">
          <span :class="{ 'text--disabled': !item.isActive }">
            {{ item.lastUsedDate ? $formatDate(item.lastUsedDate) : 'Never' }}
          </span>
        </template>

        <!-- Expires Column -->
        <template v-slot:[`item.expiresDate`]="{ item }">
          <span :class="{ 'text--disabled': !item.isActive, 'error--text': isExpired(item) }">
            {{ item.expiresDate ? $formatDate(item.expiresDate) : 'Never' }}
          </span>
        </template>

        <!-- Loading State -->
        <template v-slot:loading>
          <div class="text-center py-8">
            <v-progress-circular indeterminate color="primary" size="32" width="4" class="mb-4" />
            <p class="text-subtitle-1 font-weight-medium">Loading API Keys...</p>
          </div>
        </template>

        <!-- Empty State -->
        <template v-slot:no-data>
          <div class="text-center py-4">
            <v-icon size="64" color="grey lighten-1">mdi-key-outline</v-icon>
            <p class="mt-4 text--secondary">No API keys created yet</p>
            <v-btn  color="primary" @click="openCreateDialog">
              <v-icon left>mdi-plus</v-icon>
              Create Your First Key
            </v-btn>
          </div>
        </template>
      </v-data-table>
    </v-sheet>

    <!-- Create/Edit Dialog -->
    <api-key-dialog ref="apiKeyDialog" @created="onKeyCreated" @updated="onKeyUpdated" />

    <!-- Key Created Dialog (shows rawKey) -->
    <api-key-created-dialog ref="keyCreatedDialog" />

    <!-- Revoke Confirmation Dialog -->
    <v-dialog v-model="revokeDialog" max-width="400">
      <v-card>
        <v-card-title class="subtitle-1">
          <v-icon left small color="error">mdi-alert</v-icon>
          Revoke API Key?
        </v-card-title>
        <v-card-text class="body-2">
          <p>Are you sure you want to revoke <strong>{{ keyToRevoke?.name }}</strong>?</p>
          <p class="mb-0 grey--text">This action cannot be undone. Any applications using this key will lose access immediately.</p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text small @click="revokeDialog = false">Cancel</v-btn>
          <v-btn color="error" small :loading="revoking" @click="revokeKey">Revoke</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import apiKeysService from '@/features/Company/service/api-keys.service';
import ApiKeyDialog from '@/features/Company/components/ApiKeyDialog.vue';
import ApiKeyCreatedDialog from '@/features/Company/components/ApiKeyCreatedDialog.vue';
import { $formatDate } from '@Utils';
import { error } from '@/utils/debugLogger';
import appConfig from '../../../../appConfig';

export default {
  name: 'ApiKeysManagement',
  components: {
    ApiKeyDialog,
    ApiKeyCreatedDialog,
  },
  computed: {
    apiDocsUrl() {
      return `${appConfig.API_URL}docs/external`;
    },
  },
  data() {
    return {
      loading: false,
      apiKeys: [],
      headers: [
        { text: '', value: 'actions', width: '60px', sortable: false },
        { text: 'Name', value: 'name', width: '200px', sortable: true },
        { text: 'Permission', value: 'permission', width: '120px', sortable: true },
        { text: 'Key Prefix', value: 'keyPrefix', width: '130px', sortable: false },
        { text: 'Status', value: 'status', width: '100px', sortable: false },
        { text: 'Last Used', value: 'lastUsedDate', width: '140px', sortable: true },
        { text: 'Expires', value: 'expiresDate', width: '140px', sortable: true },
      ],
      revokeDialog: false,
      keyToRevoke: null,
      revoking: false,
    };
  },
  methods: {
    $formatDate,

    async loadKeys() {
      this.loading = true;
      try {
        const res = await apiKeysService.getAll();
        this.apiKeys = res.data || [];
      } catch (err) {
        error('Failed to load API keys:', err);
        this.$dialog.notify.error('Failed to load API keys');
      } finally {
        this.loading = false;
      }
    },

    /**
     * Determine status based on isActive and expiresDate
     */
    getStatusText(item) {
      if (!item.isActive) return 'Revoked';
      if (this.isExpired(item)) return 'Expired';
      return 'Active';
    },

    getStatusColor(item) {
      if (!item.isActive) return 'error';
      if (this.isExpired(item)) return 'warning';
      return 'success';
    },

    isExpired(item) {
      if (!item.expiresDate) return false;
      return new Date(item.expiresDate) < new Date();
    },

    openCreateDialog() {
      this.$refs.apiKeyDialog.open();
    },

    openEditDialog(item) {
      this.$refs.apiKeyDialog.open(item);
    },

    onKeyCreated(data) {
      // Add new key to list
      this.apiKeys.unshift(data);
      // Show the raw key dialog (rawKey is only in the create response)
      if (data.rawKey) {
        this.$refs.keyCreatedDialog.open(data.rawKey, data.name);
      }
    },

    onKeyUpdated(updatedKey) {
      // Update key in list
      const index = this.apiKeys.findIndex((k) => k.id === updatedKey.id);
      if (index !== -1) {
        this.$set(this.apiKeys, index, updatedKey);
      }
    },

    confirmRevoke(item) {
      this.keyToRevoke = item;
      this.revokeDialog = true;
    },

    async revokeKey() {
      if (!this.keyToRevoke) return;

      this.revoking = true;
      try {
        await apiKeysService.revoke(this.keyToRevoke.id);
        // Update status locally
        const index = this.apiKeys.findIndex((k) => k.id === this.keyToRevoke.id);
        if (index !== -1) {
          this.$set(this.apiKeys[index], 'isActive', false);
        }
        this.$dialog.notify.success('API key revoked', { position: 'top-right', timeout: 2000 });
        this.revokeDialog = false;
        this.keyToRevoke = null;
      } catch (err) {
        error('Failed to revoke API key:', err);
        this.$dialog.notify.error(err.response?.data?.error || 'Failed to revoke key');
      } finally {
        this.revoking = false;
      }
    },
  },
  mounted() {
    this.loadKeys();
  },
};
</script>

<style scoped>
.api-keys-management {
  min-height: 100%;
}

.api-keys-table {
  border-radius: 8px;
}

.key-prefix {
  font-family: 'Roboto Mono', monospace;
  font-size: 12px;
  background: rgba(0, 0, 0, 0.05);
  padding: 2px 6px;
  border-radius: 4px;
}

.opacity-50 {
  opacity: 0.5;
}

.api-docs-link {
  font-size: 13px;
  color: var(--v-primary-base);
  text-decoration: none;
  transition: opacity 0.2s;
}

.api-docs-link:hover {
  opacity: 0.8;
  text-decoration: underline;
}

/* Dark mode adjustments */
.theme--dark .key-prefix {
  background: rgba(255, 255, 255, 0.08);
}
</style>
